import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getConfig } from "../utils/config";
import { getStoryReadyTime } from "../utils/storyReadyTime";
import { getStoryUrl } from "../utils/storyUrl";
import { getBookingUrl } from "../utils/bookingUrl";
import { trackEvent } from "../utils/analytics";
import { formatStoryCountdown } from "../helpers";
import "./SessionCompleted.scss";

export const SessionCompleted: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("s");
  const sessionEndTime = searchParams.get("d");
  const storyReadyTime = sessionEndTime ? getStoryReadyTime(new Date(sessionEndTime)) : null;
  const [currentTime, setCurrentTime] = useState(() => new Date());

  const canViewStory = storyReadyTime ? currentTime >= storyReadyTime : false;

  const [storyUrl, setStoryUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!storyReadyTime || canViewStory) {
      return;
    }

    const intervalId = window.setInterval(() => setCurrentTime(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, [storyReadyTime, canViewStory]);

  useEffect(() => {
    if (canViewStory) {
      getConfig()
        .then((config) => {
          setStoryUrl(getStoryUrl(config.storyRegionDomain, sessionId));
        })
        .catch(() => {}); // fall back to "story not ready" copy
    }
  }, [canViewStory, sessionId]);

  const storyCountdown = storyReadyTime
    ? formatStoryCountdown(Math.max(0, storyReadyTime.getTime() - currentTime.getTime()))
    : null;

  return (
    <div className="pageContainer balloons-bg sessionCompletedPage">
      <div className="balloon balloon--green">
        <img src="./img/fc-balloon-green-gold.svg" alt="" />
      </div>
      <div className="balloon balloon--orange">
        <img src="./img/fc-balloon-orange-cream.webp" alt="" />
      </div>
      <div className="balloon balloon--cream">
        <img src="./img/fc-balloon-cream-red.svg" alt="" />
      </div>
      <div className="balloon balloon--purple">
        <img src="./img/fc-balloon-purple-pink.svg" alt="" />
      </div>
      <div className="logoContainer">
        <img src={"./img/fc-logo.svg"} width="125px" alt="Flight Club" />
      </div>
      <div className="contentCard-outer">
        <div className="contentCard contentCard--complete">
          <img src="./img/well-played.svg" alt="Well Played" className="wellPlayedImg" />
          {!storyUrl && (
            <>
              <div className="sessionCompletedText">
                We're putting together the best bits from your game in your own highlights reel. Check back
                when the countdown ends to see it!
                {storyCountdown && (
                  <span className="storyCountdown" aria-live="polite">
                    {storyCountdown}
                  </span>
                )}
              </div>
              <img src="./img/fc-action-replay.gif" alt="Action Replay" className="actionReplayGif" />
            </>
          )}
          {storyUrl && (
            <>
              <div className="sessionCompletedText">
                The game is over but your fun doesn't stop there. Check out the game highlights in your Story
                using the link below.
              </div>
              <div className="storyButtons">
                <a
                  className="small viewStoryLink"
                  href={`${storyUrl}?utm_source=rapid_entry&utm_medium=session_complete_story`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_story",
                      clarityEvent: "session_complete_story",
                    });
                  }}
                >
                  Enjoy your story
                </a>
                <a
                  className="small white"
                  href={`${getBookingUrl()}/?utm_source=rapid_entry&utm_medium=session_complete`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_book",
                      clarityEvent: "session_complete_book",
                    });
                  }}
                >
                  Book again
                </a>
              </div>
            </>
          )}
        </div>
        <span className="corner-bottom-left" />
        <span className="corner-bottom-right" />
      </div>
      {!storyUrl && (
        <footer className="btn-container">
          <a
            className="small"
            href={`${getBookingUrl()}/?utm_source=rapid_entry&utm_medium=session_complete`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent({
                gaEvent: "session_complete_book",
                clarityEvent: "session_complete_book",
              });
            }}
          >
            Book again
          </a>
        </footer>
      )}
    </div>
  );
};
